import { blend } from '@/features/styles/lib/colors';
import { colors as themeColors } from '@/theme/colors';
import { ExperienceColorMode } from '@/types/gql.generated';
import { setAlpha } from '@/utils/colors';
import type {
  ColorPalette,
  ColorSetLookup,
  PaletteColorName,
  PaletteColorSet,
  PaletteNeutralScale,
  PaletteNeutralStep,
  PaletteShadeStep,
} from '../color.types';
import {
  COLOR_PALETTES,
  DEFAULT_PALETTE_ID,
  PALETTE_COLOR_NAMES,
} from '../palettes';

/**
 * Retired palettes, mapped to the palette that replaced them.
 *
 * Experiences persist colorPalette as a free-form string, so a retired id stays
 * in the database indefinitely. Without this the lookup would miss and drop
 * those experiences on the default (Joyful) — which for royal would swap a deep,
 * regal planner for a bright one. Plum is the designed successor: it ships three
 * of royal's own accents (#8E1F5B, #A8451C, #1C4E7C) and the same "deep" intent.
 */
const RETIRED_PALETTE_IDS: Record<string, string> = {
  royal: 'plum',
};

/** Returns the palette with the given id, following any retirement alias, or
 *  Joyful for unknown/missing ids (colorPalette is a free-form string on the
 *  API). */
export const getColorPalette = (
  id: string | null | undefined
): ColorPalette => {
  // hasOwn, not a bare index: colorPalette is free-form on the API, so an id of
  // "toString" or "constructor" would otherwise resolve to something off the
  // prototype rather than a palette id.
  const resolvedId =
    id && Object.hasOwn(RETIRED_PALETTE_IDS, id) ? RETIRED_PALETTE_IDS[id] : id;

  return (
    COLOR_PALETTES.find((palette) => palette.id === resolvedId) ??
    COLOR_PALETTES[0]
  );
};

export const DEFAULT_PALETTE_COLOR_NAME: PaletteColorName = 'color0';

/** Coerce a persisted colorName to a valid palette slot; unknown/null map to
 *  the default slot (colorName is a free-form string on the API). */
export const toPaletteColorName = (
  colorName: string | null | undefined
): PaletteColorName =>
  PALETTE_COLOR_NAMES.includes(colorName as PaletteColorName)
    ? (colorName as PaletteColorName)
    : DEFAULT_PALETTE_COLOR_NAME;

/**
 * Canonical palette-unaware hex for a palette slot: the default palette's
 * full-strength shade. Experience category mutations dual-write this as
 * `color` alongside `colorName` so the stored fallback hex stays coherent
 * with the chosen slot.
 */
export const getCanonicalCategoryColor = (
  colorName: string | null | undefined
): string =>
  getColorPalette(DEFAULT_PALETTE_ID).colors[toPaletteColorName(colorName)]
    .shade['500'];

export const getColorSet = (
  palette: ColorPalette,
  colorName: string
): PaletteColorSet => {
  return palette.colors[toPaletteColorName(colorName)];
};

/**
 * The two stops for an accent-filled image placeholder: the identity shade
 * fading to the muted one.
 *
 * Placeholders elsewhere are a single page hue fading out via alpha. There is no
 * alpha ramp in the palette, so the accent equivalent steps 500 → 300 — design's
 * primary-to-muted pair — which keeps a placeholder's strong-to-soft depth while
 * following the chosen accent rather than the legacy page color.
 */
export const getAccentGradient = (
  palette: ColorPalette,
  colorName: string | null | undefined
): { colorStart: string; colorEnd: string } => {
  const { shade } = getColorSet(palette, colorName ?? '');
  return { colorStart: shade['500'], colorEnd: shade['300'] };
};

/** All channels at or above this read as white against a white page. */
const WHITE_CHANNEL_FLOOR = 250;

const isEffectivelyWhite = (hex: string): boolean => {
  const c = hex.replace('#', '');
  return [0, 2, 4]
    .map((i) => parseInt(c.slice(i, i + 2), 16))
    .every((channel) => channel >= WHITE_CHANNEL_FLOOR);
};

/** Perceived brightness, 0-255. */
const luma = (hex: string): number => {
  const c = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(c.slice(i, i + 2), 16));
  return 0.299 * r + 0.587 * g + 0.114 * b;
};

/** Linear hex mix, `t` of `to` over `from`. */
const mixHex = (from: string, to: string, t: number): string => {
  const channels = (hex: string) =>
    [0, 2, 4].map((i) => parseInt(hex.replace('#', '').slice(i, i + 2), 16));
  const [fr, fg, fb] = channels(from);
  const [tr, tg, tb] = channels(to);

  return `#${[
    [fr, tr],
    [fg, tg],
    [fb, tb],
  ]
    .map(([f, x]) =>
      Math.round(f + (x - f) * t)
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
};

/**
 * The slot's light wash (shade 100) when it is visible on a white page, else null.
 *
 * The accessor for *tinting a light surface* — the page canvas behind the planner
 * sheet, the Month Cards label band. It reads 100 and only 100, because 100 is the
 * light-category step in every slot of every palette: a real tint, and uniformly a
 * pale one (all 56 slots sit at luma 226 or above). The 50 step is a different role
 * and is not interchangeable — see getAccentShadedCell.
 *
 * Reaching for 50 here is what put a heavy fill behind entire planner pages: design
 * builds 50 to read *as* shading, so on the achromatic slots it goes darker than the
 * wash (sunset's gray is #C1C1C4 against a #E4E3E6 wash — 17 of the 56 slots invert
 * this way today, across all seven palettes).
 *
 * Still nullable: nothing whitens 100 today, but the guard keeps a future palette
 * from silently painting nothing, and every caller already substitutes its own
 * neutral for null.
 */
export const getAccentLightWash = (
  palette: ColorPalette,
  colorName: string | null | undefined
): string | null => {
  const { shade } = getColorSet(palette, colorName ?? '');
  return isEffectivelyWhite(shade['100']) ? null : shade['100'];
};

/** Mid-slider, so the wash is the resting look rather than the floor: below it the tint
 *  fades out to white, above it deepens toward the accent, with equal travel each way. */
export const WASH_INTENSITY = 50;

/** The slider's granularity. Lives here rather than on the control because the sentinel
 *  below is only safe while it stays coarse enough to skip it. */
export const BACKGROUND_INTENSITY_STEP = 5;

/**
 * `bold`'s column default, which on a planner means "nobody has set a strength" — a safe
 * sentinel rather than a guess, since the slider only persists multiples of
 * BACKGROUND_INTENSITY_STEP and 13 is off that grid. Reading it as the wash is what lets
 * planners keep the canvas they already render without a migration. getStyles also pins
 * the legacy HSL alpha here for the same reason.
 */
export const UNSET_BACKGROUND_STRENGTH = 13;

export const toBackgroundIntensity = (
  bold: number | null | undefined
): number =>
  bold == null || bold === UNSET_BACKGROUND_STRENGTH ? WASH_INTENSITY : bold;

const WHITE = '#ffffff';

/**
 * The slot's accent background at the given intensity (0-100): white at 0, the palette's
 * own light wash at WASH_INTENSITY — the canvas every Summer schedule has always had —
 * and the accent's identity shade (500) at 100.
 *
 * Both ends are settings rather than guarded edges: white erases the page/canvas boundary
 * and 500 reads as a saturated field the sheet floats on, which at those extremes is the
 * look being asked for. So unlike getAccentLightWash, whose null means "no usable tint",
 * this always returns a color.
 *
 * The three anchors return the palette hex verbatim rather than a mix with itself, so the
 * canvas at rest stays byte-identical to the wash it replaced.
 */
export const getAccentBackground = (
  palette: ColorPalette,
  colorName: string | null | undefined,
  intensity: number
): string => {
  const { shade } = getColorSet(palette, colorName ?? '');
  const clamped = Math.min(Math.max(intensity, 0), 100);

  if (clamped === WASH_INTENSITY) {
    return shade['100'];
  }

  if (clamped === 0) {
    return WHITE;
  }

  if (clamped === 100) {
    return shade['500'];
  }

  if (clamped < WASH_INTENSITY) {
    // Fading the wash out rather than mixing white in from the accent, so the hue
    // holds all the way down and only its strength drops.
    return mixHex(
      shade['100'],
      WHITE,
      (WASH_INTENSITY - clamped) / WASH_INTENSITY
    );
  }

  return mixHex(
    shade['100'],
    shade['500'],
    (clamped - WASH_INTENSITY) / (100 - WASH_INTENSITY)
  );
};

/**
 * The slot's full-strength solid (shade 500), or null when it reads as white.
 *
 * getAccentLightWash's Bold counterpart, for a surface painting the accent rather than
 * tinting with it. Nullable for the same reason: no palette whitens 500 today, but a
 * caller substitutes its own neutral rather than paint nothing.
 */
export const getAccentSolid = (
  palette: ColorPalette,
  colorName: string | null | undefined
): string | null => {
  const { shade } = getColorSet(palette, colorName ?? '');
  return isEffectivelyWhite(shade['500']) ? null : shade['500'];
};

/**
 * The slot's shaded-cell tint, or null when it has none.
 *
 * The accessor for a fill meant to read *as shading* — a cell struck out of the
 * grid, not a surface given a hint of the accent. Design's 50 step exists for
 * exactly this, which is why it is not simply "the lightest step": where shading has
 * to stay legible on an achromatic slot it goes darker than the 100 wash. That is
 * the point here (a blocked-out day has to out-read the weekend tint) and a bug in a
 * page-wide fill, which wants getAccentLightWash.
 *
 * The 50-then-100 fall-through is a palette disagreement, not a blend of the two
 * roles: *every* slot in professional sets 50 to #FFFFFF, so filling straight from it
 * vanishes under that palette — 100 is the nearest thing to a shaded cell it offers.
 * Retired royal had the same flaw; the sheet palettes all carry real tints. Null only
 * if a palette ever whitens both, so the caller picks its own neutral rather than
 * painting nothing.
 */
export const getAccentShadedCell = (
  palette: ColorPalette,
  colorName: string | null | undefined
): string | null => {
  const { shade } = getColorSet(palette, colorName ?? '');
  return (
    [shade['50'], shade['100']].find((step) => !isEffectivelyWhite(step)) ??
    null
  );
};

/**
 * The neutral step muted ink takes, off the ramp the slot is tied to.
 *
 * Fixed rather than derived. This used to walk the ramp lighter until it cleared
 * the accent's luma, on the reasoning that on a white page quieter means lighter
 * — which holds only while the accent is darker than the step. It isn't for the
 * light accents: joyful's and primary's yellows both sit above 400, so the walk
 * stepped past it to 300 (1.36:1 on white) and cloud's slots to 300 or 100
 * (1.17:1), landing the weekend date somewhere between faint and invisible.
 *
 * Legibility wins over the strict lighter-than-the-accent ordering: a light
 * accent leaves no room underneath it, so muting there is carried by the drop to
 * an achromatic grey rather than by lightness. The cost is that under a pastel
 * accent the muted date reads a touch heavier than the weekday beside it.
 */
const MUTED_NEUTRAL_STEP: PaletteNeutralStep = '400';

export const getMutedNeutral = (neutral: PaletteNeutralScale): string =>
  neutral[MUTED_NEUTRAL_STEP];

/**
 * Resolves a dot-notation color-set lookup (e.g. "shade.500", "neutral.200")
 * for the given color name against a palette. The group and step segments are
 * type-constrained; colorName is free-form (e.g. the persisted
 * experience.primaryColor), and unknown names resolve to the default slot.
 */
export const getPaletteLookupColor = (
  palette: ColorPalette,
  colorName: string,
  lookup: ColorSetLookup
): string => {
  const [group, step] = lookup.split('.') as ['neutral' | 'shade', string];
  const colorSet = getColorSet(palette, colorName);
  return group === 'neutral'
    ? colorSet.neutral[step as PaletteNeutralStep]
    : colorSet.shade[step as PaletteShadeStep];
};

/**
 * The canonical category color lookup: the palette specified by the
 * experience (falling back to joyful), the color-set specified by the
 * category's colorName, and the shade specified by the experience's
 * colorMode (LIGHT -> the 100 wash, otherwise the 500 identity). Persisted
 * colorNames are normalized to a valid slot via toPaletteColorName at the
 * call boundary.
 */
export const resolveCategoryColor = (
  colorName: PaletteColorName,
  paletteId: string | null | undefined,
  colorMode: ExperienceColorMode | null | undefined
): string => {
  const { shade } = getColorPalette(paletteId).colors[colorName];
  return colorMode === ExperienceColorMode.Light ? shade['100'] : shade['500'];
};

export type CategoryFill = {
  /** Solid background fill. */
  background: string;
  /** Readable text color on that background. */
  text: string;
};

/**
 * Mode-aware solid card/chip fill, following the canonical category color
 * lookup: LIGHT renders the 100 wash with the ink text color (the same-hue
 * 500 shade is often illegible on the wash); otherwise the full-strength 500
 * with a luminance-picked text color.
 */
export const resolveCategoryFill = (
  colorName: PaletteColorName,
  paletteId: string | null | undefined,
  colorMode: ExperienceColorMode | null | undefined
): CategoryFill => {
  const { shade } = getColorPalette(paletteId).colors[colorName];
  return colorMode === ExperienceColorMode.Light
    ? { background: shade['100'], text: themeColors.ink.strong }
    : { background: shade['500'], text: cardTextOn(shade['500']) };
};

export type CategoryOptionColors = {
  backgroundColor: string;
  hoverBackgroundColor: string;
  textColor: string;
};

/**
 * Mode-aware fill for a category *option* row (the CategorySelect list):
 * resolveCategoryFill's background/text plus a slightly darkened hover.
 */
export const resolveCategoryOptionColors = (
  colorName: PaletteColorName,
  paletteId: string | null | undefined,
  colorMode: ExperienceColorMode | null | undefined
): CategoryOptionColors => {
  const fill = resolveCategoryFill(colorName, paletteId, colorMode);
  return {
    backgroundColor: fill.background,
    // Composite 8% black over the solid fill for a subtle hover darken.
    hoverBackgroundColor: blend(setAlpha('#000000', 0.08), fill.background),
    textColor: fill.text,
  };
};

/**
 * Readable foreground color for text rendered on top of the given hex
 * background (luminance threshold 165/255).
 */
export const cardTextOn = (hex: string): string =>
  luma(hex) > 165 ? '#1b1a16' : '#ffffff';

/** The step readable ink darkens toward — cardTextOn's dark pairing. */
const DARK_INK = '#1b1a16';

/** WCAG relative luminance, 0-1. Not `luma` above: that is a cheap 0-255
 *  brightness proxy, and contrast ratios need the gamma-corrected form. */
const relativeLuminance = (hex: string): number => {
  const c = hex.replace('#', '');
  const [r, g, b] = [0, 2, 4]
    .map((i) => parseInt(c.slice(i, i + 2), 16) / 255)
    .map((channel) =>
      channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
    );

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** WCAG contrast ratio between two opaque colors, 1-21. */
export const contrastRatio = (a: string, b: string): number => {
  const [lighter, darker] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x
  );

  return (lighter + 0.05) / (darker + 0.05);
};

/** WCAG AA for normal-size text — event titles are body-sized, so the large-text
 *  3:1 allowance does not apply. */
const MIN_INK_CONTRAST = 4.5;

const STEPS = 20;

/**
 * A category hue made safe to use as text ink on the given surface.
 *
 * The 500 step is built as a *fill*, read by its area, and the pale slots are
 * nowhere near legible as text — cloud's yellow is 1.31:1 on white against the
 * 4.5:1 body text needs, so a title painted with it disappears.
 *
 * Darkening rather than swapping in a neutral is what keeps the signal: a title
 * dropped to a dark amber still reads as the yellow category beside a red one,
 * where flat dark ink would say nothing. Hues already clearing the ratio are
 * returned untouched, so the saturated palettes are unaffected.
 */
export const readableInkOn = (hue: string, surface: string): string => {
  if (contrastRatio(hue, surface) >= MIN_INK_CONTRAST) {
    return hue;
  }

  // Mixed in hex, not via `blend` — that returns an rgba() string the ratio math
  // cannot parse, which silently sent every hue to the DARK_INK fallback.
  for (let step = 1; step < STEPS; step++) {
    const candidate = mixHex(hue, DARK_INK, step / STEPS);

    if (contrastRatio(candidate, surface) >= MIN_INK_CONTRAST) {
      return candidate;
    }
  }

  return DARK_INK;
};
